/**
 * Per-matter Economics overview (US #2238): KPIs (#2272), charts (#2273),
 * breakdowns (#2274) and CSV export (#2275).
 *
 * Reads GET /matters/<uuid>/economics for the rollup and
 * GET /matters/<uuid>/time-entries for the shape of the work over time - the
 * rollup is a set of totals and cannot answer "when". Both are the same
 * endpoints the Time keeping sub-tab already talks to; no new data layer.
 *
 * Layout, top to bottom: the four figures, then budget consumption beside the
 * time series, then who did the work beside what it was. Everything sits on
 * the standard liquid glass (`GlassCard`) with the brand chart palette, so the
 * whole tab is one surface rather than a stack of unrelated widgets.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { readBudget } from './budgetModel';
import { useTranslation } from 'react-i18next';
import {
  PiChartLineUpLight,
  PiChartPieSliceLight,
  PiCurrencyDollarLight,
  PiDownloadSimpleLight,
  PiHourglassLight,
  PiPencilSimpleLineLight,
  PiReceiptLight,
  PiWalletLight,
} from 'react-icons/pi';
import { RootState, useAppDispatch } from '../../../store';
import { showSidePanel, hideSidePanel } from '../../../store/actions/sidePanelActions';
import { useOrgDefaultCurrency } from '../../../hooks/useOrgDefaultCurrency';
import {
  fetchActivityTypes,
  fetchMatterEconomics,
  fetchTimeEntries,
} from '../../../store/actions/economicsActions';
import { formatCurrency } from '../../../utils/formatCurrency';
import Button from '../../shared/TailwindComponents/Button';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import GlassCard from '../../shared/brand/GlassCard';
import { KpiTile } from '../../shared/brand/Field';
import { downloadCsv } from '../../../utils/csv';
import {
  ActivityDonut,
  BudgetDonut,
  FeeEarnerBars,
  RecordedTimeChart,
  TimePoint,
} from './EconomicsCharts';
import MatterPricingSummary from './MatterPricingSummary';
import type { MatterData } from '../MatterDetails';
import type { ActivityType, MatterEconomicsSummary, TimeEntry } from '../../../types/economics';

interface MatterEconomicsTabProps {
  matterData: MatterData | null;
  /** Gates the pricing operation - the matter owner or a firm admin. */
  userAccessLevel?: 'edit' | 'admin' | 'view';
  /** Apply a saved pricing patch to the matter the page is holding. */
  onUpdate?: (patch?: Record<string, any>) => void;
}

/** `2026-03-14` → `2026-03`. Entry dates are plain YMD, so this is a slice. */
const monthKey = (ymd: string) => (ymd || '').slice(0, 7);

const MatterEconomicsTab: React.FC<MatterEconomicsTabProps> = ({
  matterData,
  userAccessLevel = 'view',
  onUpdate,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const { defaultCurrency: orgDefaultCurrency } = useOrgDefaultCurrency();
  const canManagePricing = userAccessLevel === 'admin' || !!userInfo?.is_org_admin;
  const matterUuid = matterData?.uuid || '';

  const [data, setData] = useState<MatterEconomicsSummary | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const collaboratorName = useMemo(() => {
    const byId = new Map<string, string>();
    (matterData?.Collaborators || []).forEach((c) => byId.set(String(c.UserID), c.FullName || c.Email || `User ${c.UserID}`));
    return (userId: number) => byId.get(String(userId)) || `User ${userId}`;
  }, [matterData?.Collaborators]);

  const activityLabel = useMemo(() => {
    const byId = new Map<number, string>();
    activityTypes.forEach((a) => byId.set(a.id, a.label));
    return (id: number) => (byId.has(id) ? t(byId.get(id)!) : `#${id}`);
  }, [activityTypes, t]);

  useEffect(() => {
    dispatch(fetchActivityTypes()).then(setActivityTypes);
  }, [dispatch]);

  const load = useCallback(async () => {
    if (!matterUuid) return;
    setLoading(true);
    setError(false);
    try {
      // The rollup is the tab's contract; the entries only feed the time
      // series, so a failure there must not blank the whole tab.
      const [res, timeEntries] = await Promise.all([
        dispatch(fetchMatterEconomics(matterUuid)),
        dispatch(fetchTimeEntries(matterUuid)).catch(() => [] as TimeEntry[]),
      ]);
      setData(res || null);
      setEntries(Array.isArray(timeEntries) ? timeEntries : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, matterUuid]);

  useEffect(() => {
    load();
  }, [load]);

  const currency = data?.currency || matterData?.currency || 'eur';
  const money = (v?: number | null) => formatCurrency(Number(v || 0), currency);
  const hrs = (v?: number | null) => `${Number(v || 0).toFixed(2)} ${t('h')}`;

  // Display-ready breakdowns. `user` keeps the enriched row for UserCell; `label`
  // is the plain name used by the CSV export.
  const byUser = useMemo(
    () => (data?.by_user || []).map((u) => ({
      user: u,
      label: u.name || collaboratorName(u.user_id),
      hours: u.hours,
      revenue: u.revenue,
    })).sort((a, b) => b.hours - a.hours),
    [data, collaboratorName],
  );
  const byActivity = useMemo(
    () => (data?.by_activity || []).map((a) => ({ label: activityLabel(a.activity_type_id), hours: a.hours })),
    [data, activityLabel],
  );

  /**
   * Hours per month, oldest first, with no gaps: a month in which nothing was
   * recorded is a zero bar, not a missing column, or the axis lies about pace.
   */
  const timeSeries = useMemo<TimePoint[]>(() => {
    const buckets = new Map<string, { hours: number; billableHours: number }>();
    entries.forEach((entry) => {
      const key = monthKey(entry.entry_date);
      if (!key) return;
      const bucket = buckets.get(key) || { hours: 0, billableHours: 0 };
      const hours = Number(entry.hours_decimal || 0);
      bucket.hours += hours;
      if (entry.billable) bucket.billableHours += hours;
      buckets.set(key, bucket);
    });
    if (buckets.size === 0) return [];

    const keys = Array.from(buckets.keys()).sort();
    const filled: string[] = [];
    const [firstYear, firstMonth] = keys[0].split('-').map(Number);
    const [lastYear, lastMonth] = keys[keys.length - 1].split('-').map(Number);
    const cursor = new Date(Date.UTC(firstYear, firstMonth - 1, 1));
    const end = new Date(Date.UTC(lastYear, lastMonth - 1, 1));
    while (cursor <= end && filled.length < 36) {
      filled.push(cursor.toISOString().slice(0, 7));
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }

    return filled.map((key) => ({
      key,
      label: key,
      hours: buckets.get(key)?.hours ?? 0,
      billableHours: buckets.get(key)?.billableHours ?? 0,
    }));
  }, [entries]);

  const currentMonthKey = useMemo(() => new Date().toISOString().slice(0, 7), []);

  /*
    Budget / billed / WIP / remaining, from the shared reading so this strip,
    the donut and the Details mini card cannot disagree.

    The denominator is the rollup's `budget_amount` - which the server backs
    with `fixed_fee_amount` when no explicit budget is set - so a budget now
    exists for hourly and contingency matters too, not only fixed-fee ones.
    The remainder is `budget_remaining`, NOT `fixed_fee_remaining`: the two
    answer different questions and are near-unrelated on a fixed-fee matter.
  */
  const reading = useMemo(
    () => readBudget(data, matterData?.budget_amount ?? matterData?.fixed_fee_amount ?? null),
    [data, matterData?.budget_amount, matterData?.fixed_fee_amount]
  );
  const { budget, billed, wip, remaining } = reading;

  /*
    The pricing OPERATION, performed here rather than on Details (mat-19).
    Same resolution order the read view uses, so the form cannot open on a
    different currency or budget than the card beside it is showing.
  */
  const pricingCurrency = (
    matterData?.currency || data?.currency || orgDefaultCurrency || ''
  ).toString().toLowerCase();
  const pricingBudget = matterData?.budget_amount ?? data?.budget_amount ?? null;

  const openPricingPanel = () => {
    dispatch(showSidePanel({
      type: 'matterPricing',
      showSidePanel: true,
      size: 'md',
      message: t('Pricing & Economics'),
      icon: <PiCurrencyDollarLight className="w-6 h-6 text-gray-600" />,
      subMsg: t('Set how this matter is billed and in which currency.'),
      matterData,
      currency: pricingCurrency,
      budgetAmount: pricingBudget != null ? Number(pricingBudget) : null,
      onSaved: (patch: Record<string, any>) => {
        // The rollup carries the currency and the remainder, so a pricing
        // change has to re-read it - patching only the matter would leave
        // every figure on this tab formatted in the OLD currency.
        onUpdate?.(patch);
        load();
      },
      onCancel: () => dispatch(hideSidePanel()),
    }));
  };

  const exportCsv = () => {
    const headers = [t('Collaborator'), t('Hours'), `${t('Revenue')} (${currency.toUpperCase()})`];
    const rows = byUser.map((u) => [u.label, u.hours.toFixed(2), u.revenue.toFixed(2)]);
    rows.push([t('Total'), Number(data?.total_hours || 0).toFixed(2), Number(data?.revenue || 0).toFixed(2)]);
    const slug = (matterData?.MatterName || 'matter').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
    downloadCsv(`economics-${slug}`, headers, rows);
  };

  if (error) {
    return (
      <div className="p-6 text-sm text-fg-muted text-center">
        {t('Could not load economics for this matter.')}{' '}
        <button className="text-primary-600 underline" onClick={load}>{t('Retry')}</button>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="kpi" />)}
        </div>
        {/* Same three-then-two rhythm the loaded tab has, so nothing jumps. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton variant="card" />
          <Skeleton variant="card" />
          <Skeleton variant="card" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton variant="card" />
          <Skeleton variant="card" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-fg-muted">
          {t('Total hours')}: <span className="tabular-nums text-fg-body">{hrs(data?.total_hours)}</span>
          <span className="mx-1.5">·</span>
          {t('Billable')}: <span className="tabular-nums text-fg-body">{hrs(data?.billable_hours)}</span>
        </p>
        <Button variant="outline" size="small" icon={<PiDownloadSimpleLight />} onClick={exportCsv} disabled={!byUser.length}>
          {t('Export CSV')}
        </Button>
      </div>

      {/* ── The four figures the matter is judged on ──────────────
          Budget is the agreed fee; billed is what has actually been
          invoiced; WIP is recorded work that has not been invoiced yet
          (revenue − invoiced); remaining is the backend's own
          `fixed_fee_remaining`, never recomputed here. */}
      <GlassCard padding="none">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-hairline-soft">
          <KpiTile
            icon={<PiWalletLight />}
            label={t('Budget')}
            value={budget != null ? money(budget) : '—'}
          />
          <KpiTile icon={<PiReceiptLight />} label={t('Billed')} value={money(billed)} />
          <KpiTile icon={<PiHourglassLight />} label={t('WIP')} value={money(wip)} />
          <KpiTile
            icon={<PiChartLineUpLight />}
            label={t('Remaining')}
            // Negative on overrun, and shown that way rather than floored.
            value={remaining != null ? money(remaining) : '—'}
            tone={remaining != null && remaining < 0 ? 'danger' : 'default'}
          />
        </div>
      </GlassCard>

      {/* ── How it is billed, what is consumed, at what pace ──────
          ONE row of three equal columns, all stretched to the same height:
          the tab used to leave a ragged step between a short donut card and
          a tall chart card, and a whole empty column when a matter had no
          budget. The time series takes the freed column instead. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GlassCard
          fill
          icon={<PiCurrencyDollarLight />}
          title={t('Pricing')}
          subtitle={t('How this matter is billed.')}
          actions={canManagePricing && (
            <Button
              variant="primary"
              size="small"
              icon={<PiPencilSimpleLineLight />}
              onClick={openPricingPanel}
              disabled={!matterUuid}
            >
              {t('Edit')}
            </Button>
          )}
        >
          <MatterPricingSummary
            matterData={matterData}
            currency={pricingCurrency}
            budgetAmount={pricingBudget != null ? Number(pricingBudget) : null}
            economics={data}
            canManage={canManagePricing}
          />
        </GlassCard>

        {reading.hasBudget && (
          <GlassCard
            fill
            icon={<PiChartPieSliceLight />}
            title={t('Budget consumption')}
            subtitle={`${money(billed + wip)} / ${money(budget)}`}
          >
            <BudgetDonut
              budget={Number(budget)}
              billed={billed}
              wip={wip}
              currency={currency}
              usedPct={reading.usedPct ?? 0}
              remaining={remaining}
            />
            <div className="mt-3 flex items-center justify-center gap-4 text-[10px] text-fg-muted">
              <span className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-champagne" />{t('Billed')}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-champagne/40" />{t('WIP')}
              </span>
            </div>
          </GlassCard>
        )}

        <GlassCard
          fill
          className={reading.hasBudget ? undefined : 'lg:col-span-2'}
          bodyClassName="flex flex-col justify-center"
          icon={<PiChartLineUpLight />}
          title={t('Recorded time by month')}
          subtitle={t('The current month is still open — shown at WIP weight.')}
        >
          <RecordedTimeChart series={timeSeries} currentKey={currentMonthKey} />
        </GlassCard>
      </div>

      {/* ── Who, and on what ────────────────────────────────────────
          Both cards fill the row, so a short activity donut no longer
          leaves a gap beside a long fee-earner list. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FeeEarnerBars rows={byUser} currency={currency} fill />
        <ActivityDonut rows={byActivity} fill />
      </div>
    </div>
  );
};

export default MatterEconomicsTab;
